(function (root) {
  'use strict';
  const KEY = 'sac.garage.selection.v1';
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const empty = () => ({signedIn: false, garageAvailable: true, ownerKey: '', garageUpdatedAt: '', primaryVehicleId: null, vehicles: []});
  const text = (value, max) => typeof value === 'string' && value.length <= max && !/[<>\u0000-\u001f\u007f]/.test(value);
  function normalize(value) {
    if (!value || value.schemaVersion !== 1 || value.signedIn !== true) return empty();
    const state = {...empty(), signedIn: true, garageAvailable: false};
    if (!/^\d+$/.test(String(value.ownerKey || ''))) return state;
    state.ownerKey = String(value.ownerKey);
    if (value.garageAvailable === false || !Array.isArray(value.vehicles) || value.vehicles.length > 25) return state;
    const ids = new Set();
    for (const row of value.vehicles) {
      if (!row || !UUID.test(row.id) || ids.has(row.id) || !Number.isInteger(row.year) || row.year < 1886 || row.year > new Date().getFullYear() + 2 || !text(row.make, 60) || !row.make.trim() || !text(row.model, 100) || !row.model.trim() || !text(row.trim || '', 100) || !text(row.engine || '', 120) || !text(row.nickname || '', 60) || !text(row.updatedAt || '', 30)) return state;
      ids.add(row.id);
    }
    if (value.vehicles.length && !ids.has(value.primaryVehicleId)) return state;
    return {...state, garageAvailable: true, garageUpdatedAt: text(value.garageUpdatedAt || '', 30) ? value.garageUpdatedAt || '' : '', primaryVehicleId: value.primaryVehicleId || null, vehicles: value.vehicles.map(row => ({id: row.id, year: row.year, make: row.make, model: row.model, trim: row.trim || '', engine: row.engine || '', nickname: row.nickname || '', updatedAt: row.updatedAt || ''}))};
  }
  function choose(state, remembered) {
    if (!state.signedIn || !state.garageAvailable) return null;
    if (remembered?.ownerKey === state.ownerKey) {
      if (remembered.manual === true) return null;
      if (remembered.explicit === true && state.vehicles.some(row => row.id === remembered.selectedId)) return remembered.selectedId;
    }
    return state.primaryVehicleId;
  }
  function createStore(storage) {
    let state = empty(), selectedId = null, pending = false, failed = false;
    const listeners = new Set();
    const read = () => { try { return JSON.parse(storage?.getItem(KEY) || 'null'); } catch (_) { return null; } };
    const referenceValid = reference => reference && ['year', 'make', 'model', 'engine'].every(key => /^\d{1,12}$/.test(String(reference[key] || '')));
    const references = () => {
      const remembered = read();
      if (remembered?.ownerKey !== state.ownerKey) return {};
      const result = {};
      state.vehicles.forEach(vehicle => {
        const saved = remembered.guideReferences?.[vehicle.id];
        if (saved?.updatedAt === vehicle.updatedAt && referenceValid(saved.reference) && Number(saved.reference.year) === vehicle.year) result[vehicle.id] = saved;
      });
      return result;
    };
    const persist = (manual, explicit = false) => { try { if (!state.signedIn) storage?.removeItem(KEY); else if (state.garageAvailable) storage?.setItem(KEY, JSON.stringify({ownerKey: state.ownerKey, selectedId, manual, explicit, guideReferences: references()})); } catch (_) {} };
    const getVehicle = () => pending || failed ? null : state.vehicles.find(row => row.id === selectedId) || null;
    const snapshot = () => ({signedIn: state.signedIn, garageAvailable: state.garageAvailable, vehicles: pending || failed ? [] : state.vehicles.map(row => ({...row})), vehicle: getVehicle() ? {...getVehicle()} : null, selectedId, pending, failed});
    const emit = reason => listeners.forEach(fn => fn(snapshot().vehicle, {reason}));
    return {
      getState: snapshot,
      getVehicle: () => snapshot().vehicle,
      subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
      getGuideReference(id) { const saved = references()[id]; return saved ? {...saved.reference} : null; },
      setGuideReference(id, reference) {
        if (reference === null) { const current = read(); if (current?.ownerKey !== state.ownerKey) return false; const guideReferences = references(); delete guideReferences[id]; try { storage?.setItem(KEY, JSON.stringify({...current, guideReferences})); return true; } catch (_) { return false; } }
        const vehicle = state.vehicles.find(row => row.id === id);
        if (pending || failed || !vehicle || selectedId !== id || !referenceValid(reference) || Number(reference.year) !== vehicle.year) return false;
        const current = read(); const guideReferences = references();
        guideReferences[id] = {updatedAt: vehicle.updatedAt, reference: Object.fromEntries(['year', 'make', 'model', 'engine'].map(key => [key, String(reference[key])]))};
        try { storage?.setItem(KEY, JSON.stringify({ownerKey: state.ownerKey, selectedId, manual: current?.manual === true, explicit: current?.explicit === true, guideReferences})); return true; } catch (_) { return false; }
      },
      clearGuideReference(id) { const current = read(); if (current?.ownerKey !== state.ownerKey) return; const guideReferences = references(); delete guideReferences[id]; try { storage?.setItem(KEY, JSON.stringify({...current, guideReferences})); } catch (_) {} },
      apply(payload, reason = 'load') {
        const previous = JSON.stringify(snapshot());
        const remembered = read();
        state = normalize(payload); pending = false; failed = false; selectedId = choose(state, remembered);
        persist(remembered?.ownerKey === state.ownerKey && remembered.manual === true, remembered?.ownerKey === state.ownerKey && remembered.explicit === true);
        if (JSON.stringify(snapshot()) !== previous) emit(reason);
      },
      select(id) {
        if (pending || failed || !state.signedIn || !state.garageAvailable) return false;
        if (id && !state.vehicles.some(row => row.id === id)) return false;
        selectedId = id || null; persist(!id, true); emit('selection'); return true;
      },
      invalidate() { pending = true; failed = false; emit('refreshing'); },
      fail() { pending = false; failed = true; state = empty(); selectedId = null; try { storage?.removeItem(KEY); } catch (_) {} emit('unavailable'); }
    };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {normalize, choose, createStore, KEY};
  if (!root?.document) return;
  const document = root.document;
  function parse(node) { try { return JSON.parse(node?.textContent || 'null'); } catch (_) { return null; } }
  const source = document.getElementById('SacCustomerGarageData');
  if (!source) return;
  const payload = parse(source);
  let storage; try { storage = root.sessionStorage; } catch (_) {}
  const store = createStore(storage);
  const strings = parse(document.getElementById('SacCustomerGarageStrings')) || {};
  const t = key => strings[key] || '';
  const garageUrl = 'https://shopify.com/59377975384/account/pages/01a1611e-155b-746c-b6aa-008618c9fde7';
  let loginUrl = payload?.loginUrl || '/customer_authentication/login';
  let refreshing = null, refreshGeneration = 0, suspended = false;
  function element(tag, value, className) { const node = document.createElement(tag); if (value) node.textContent = value; if (className) node.className = className; return node; }
  function render() {
    const state = store.getState();
    document.querySelectorAll('[data-sac-garage-panel]').forEach(panel => {
      const focused = panel.contains(document.activeElement);
      panel.replaceChildren();
      const title = element('strong', t('title'), 'sac-garage__title'); panel.append(title);
      const body = element('div', '', 'sac-garage__body'); panel.append(body);
      if (state.pending) { body.append(element('span', t('refreshing'))); return; }
      if (state.failed || !state.garageAvailable) {
        body.append(element('span', t('unavailable')));
        const retry = element('button', t('retry'), 'sac-garage__link'); retry.type = 'button'; retry.addEventListener('click', refresh); body.append(retry); return;
      }
      if (!state.signedIn) {
        body.append(element('span', t('signed_out')));
        const link = element('a', t('sign_in'), 'sac-garage__link'); link.href = loginUrl; body.append(link); return;
      }
      if (!state.vehicles.length) {
        body.append(element('span', t('empty')));
      } else {
        const label = element('label', t('shopping_for'), 'sac-garage__label');
        const select = element('select', '', 'sac-garage__select');
        select.setAttribute('aria-label', t('shopping_for'));
        const manual = element('option', t('manual')); manual.value = ''; select.append(manual);
        state.vehicles.forEach(vehicle => { const option = element('option', `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.nickname ? ' · ' + vehicle.nickname : ''}`); option.value = vehicle.id; select.append(option); });
        select.value = state.selectedId || ''; select.addEventListener('change', () => store.select(select.value)); label.append(select); body.append(label);
        const status = element('span', state.vehicle ? t('selected_hint') : t('manual_hint'), 'sac-garage__hint'); status.setAttribute('role', 'status'); body.append(status);
      }
      const manage = element('a', state.vehicles.length ? t('manage') : t('add'), 'sac-garage__link'); manage.href = garageUrl; body.append(manage);
      if (focused) panel.querySelector('select,button,a')?.focus();
    });
  }
  function suspend() {
    suspended = true;
    refreshGeneration += 1;
    refreshing?.controller.abort();
    refreshing = null;
    source.textContent = '';
    store.invalidate();
  }
  function refresh() {
    if (document.hidden) { suspend(); return Promise.resolve(); }
    if (refreshing) return refreshing.promise;
    const request = {generation: ++refreshGeneration, controller: new AbortController(), promise: null};
    const current = () => refreshing === request && request.generation === refreshGeneration;
    refreshing = request;
    source.textContent = '';
    store.invalidate();
    request.promise = (async () => {
      const timer = root.setTimeout(() => request.controller.abort(), 10000);
      try {
        const url = new URL(root.location.href); const preview = url.searchParams.get('preview_theme_id'); url.search = ''; url.hash = ''; if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview); url.searchParams.set('section_id', 'sac-customer-garage-data');
        const response = await root.fetch(url.href, {credentials: 'same-origin', cache: 'no-store', signal: request.controller.signal});
        if (!current()) return;
        if (!response.ok) throw new Error('Garage refresh failed');
        const body = await response.text();
        if (!current()) return;
        if (request.controller.signal.aborted) throw new Error('Garage refresh timed out');
        const html = new DOMParser().parseFromString(body, 'text/html');
        const node = html.getElementById('SacCustomerGarageData'); const next = parse(node);
        if (!next || next.schemaVersion !== 1) throw new Error('Invalid garage response');
        if (next.loginUrl) loginUrl = next.loginUrl;
        source.textContent = JSON.stringify(next); store.apply(next, 'refresh');
      } catch (_) {
        if (current()) { source.textContent = ''; store.fail(); }
      } finally {
        root.clearTimeout(timer);
        if (current()) refreshing = null;
      }
    })();
    return request.promise;
  }
  store.apply(payload);
  root.SacGarageSelection = Object.freeze({getVehicle: store.getVehicle, getState: store.getState, subscribe: store.subscribe, select: store.select, getGuideReference: store.getGuideReference, setGuideReference: store.setGuideReference, clearGuideReference: store.clearGuideReference, refresh});
  store.subscribe((vehicle, metadata) => { render(); root.dispatchEvent(new CustomEvent('sac:garage:changed', {detail: {vehicle, reason: metadata.reason}})); });
  render();
  root.addEventListener('pagehide', suspend);
  root.addEventListener('pageshow', event => { if (!document.hidden && (event.persisted || suspended)) { suspended = false; refresh(); } });
  document.addEventListener('visibilitychange', () => { if (document.hidden) suspend(); else if (suspended) { suspended = false; refresh(); } });
})(typeof window === 'undefined' ? null : window);
